import {
  ContractReference,
  ContractReferenceType
} from 'Cube/domain/cube.domain.types'
// The `Integrations` index imports Cube, so we go straight to the file here.
import { LinkSalesforceOpportunityModal } from 'Integrations/integrationsConfig/salesforce/InspectorItem/widgets/LinkSalesforceOpportunityWidget/LinkSalesforceOpportunityModal/LinkSalesforceOpportunityModal'
import { FC } from 'react'
import { match } from 'ts-pattern'
import { ContractTagModal } from '../contractTag.modal'
import {
  getReferenceAt,
  getReferenceModalCopy
} from './contractReferences.helpers'
import { OtherReferenceModal } from './otherReference.modal'
import { QuoteReferenceModal } from './quoteReference.modal'
import { usePhaseContractReferences } from './usePhaseContractReferences'

/**
 * Adds a reference of the given type to a phase, or changes the one at
 * `index`. The phase header and the integration tabs both open this.
 */
export const ContractReferenceModal: FC<{
  phaseId: string
  type: ContractReferenceType
  /** Where the reference sits in the phase's list, when changing one. */
  index?: number
  onClose: () => void
}> = props => {
  const contractReferences = usePhaseContractReferences({
    phaseId: props.phaseId
  })
  const existingReference = getReferenceAt(
    contractReferences.allReferences,
    props.index
  )

  const upsert = (reference: ContractReference) => {
    if (props.index === undefined) {
      contractReferences.addReference(reference)
      return
    }

    contractReferences.updateReference(props.index, reference)
  }

  /**
   * Links (and a quote's number) only come back from the API for what is
   * saved. So an unchanged value keeps the saved reference, while a new value
   * has no link until the schedule is saved and reloaded.
   */
  const saveValue = ({ value, label }: { value: string; label: string }) => {
    const isUnchanged = existingReference?.value === value

    upsert(
      isUnchanged && existingReference
        ? existingReference
        : { type: props.type, value, label, url: null }
    )
  }

  return match(props.type)
    .with('SEQUENCE_QUOTE', () => (
      <QuoteReferenceModal
        customerId={contractReferences.customerId}
        initialReference={existingReference}
        onSubmit={quote =>
          saveValue({ value: quote.id, label: quote.quoteNumber })
        }
        onClose={props.onClose}
      />
    ))
    .with('SALESFORCE_OPPORTUNITY', () => (
      <LinkSalesforceOpportunityModal
        {...getReferenceModalCopy({
          isChanging: Boolean(existingReference),
          noun: 'Salesforce opportunity'
        })}
        initialOpportunityId={existingReference?.value}
        onLink={({ opportunityId }) =>
          saveValue({ value: opportunityId, label: opportunityId })
        }
        onClose={props.onClose}
      />
    ))
    .with('NETSUITE', () => (
      <ContractTagModal
        initialValue={existingReference?.value}
        onSubmit={contractTag =>
          saveValue({ value: contractTag, label: contractTag })
        }
        onClose={props.onClose}
      />
    ))
    .with('OTHER', () => (
      <OtherReferenceModal
        references={contractReferences.allReferences}
        editingIndex={props.index}
        // The link is the user's own text here, so it's always taken as typed.
        onSubmit={({ name, link }) =>
          upsert({ type: 'OTHER', value: name, label: name, url: link })
        }
        onClose={props.onClose}
      />
    ))
    .exhaustive()
}
